/**
 * PlayerActivity — right-aligned stack of live round events (server snapshots).
 * Single surface for the feed: same logic as former wheel bubbles, stored in Zustand per room.
 */

import { AnimatePresence, LayoutGroup, motion } from "framer-motion"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import {
  type BubbleGiftEntry,
  type DerivedBubbleEvent,
} from "@/entities/pvp-roulette"
import type { BetUserData } from "@/shared/api/types/pvpRoulette"
import { useI18n } from "@/shared/i18n"
import { getAvatarInitials } from "@/shared/lib/avatarInitials"
import { resolveAvatarLoadUrl } from "@/shared/lib/avatarProxy"
import { usePvpRoomActivityFeed } from "../hooks/usePvpRoomSelectors"
import { GiftBetThumbnails } from "./GiftBetThumbnails"
import { PvpTonAmountPill } from "./PvpTonAmountPill"
import { cn } from "@/shared/lib"

interface PlayerActivityProps {
  roomId: string
}

function displayName(user: BetUserData, fallback: string): string {
  const u = user.username?.trim()
  if (u) return u
  return user.userId.length >= 6 ? user.userId.slice(0, 6) : fallback
}

const VISUAL = {
  opacities: [1, 0.78, 0.55] as const,
}

/** Rows + gaps + top padding + bottom bleed for exit motion (must match `PvPRoomLayout` reserve). */
const BUBBLE_GAP_PX = 4
const BUBBLE_SLOT_PT_PX = 4
/** Space under the stack so the bottom row’s exit `y` + shadow are not clipped by `overflow-hidden`. */
const BUBBLE_EXIT_BLEED_PX = 48
/** Base (unscaled) capsule row height — inner is scaled; outer row height is this × scale so gaps stay true. */
const BUBBLE_BASE_ROW_PX = 40
/** Each step down the stack multiplies visual size by this (layout row height follows the same factor). */
const BUBBLE_RELATIVE_SCALE = 0.85

/** One full-size bubble; depth handled via `scale` + layout-matched outer height. */
const BUBBLE_AVATAR = "h-7 w-7 shrink-0 border border-white"
const BUBBLE_AVATAR_FALLBACK = "text-[8px] text-foreground"

function bubbleLayoutScale(bubblesBefore: number): number {
  return Math.pow(BUBBLE_RELATIVE_SCALE, bubblesBefore)
}

function bubbleRowLayoutHeightPx(bubblesBefore: number): number {
  const s = bubbleLayoutScale(bubblesBefore)
  return Math.round(BUBBLE_BASE_ROW_PX * s * 1000) / 1000
}

/** Must match the sum of `bubbleRowLayoutHeightPx(d, false)` for the three visible depths. */
const BUBBLE_STACK_CONTENT_MAX_PX =
  bubbleRowLayoutHeightPx(0) +
  bubbleRowLayoutHeightPx(1) +
  bubbleRowLayoutHeightPx(2)
const BUBBLE_STACK_MAX_PX = BUBBLE_STACK_CONTENT_MAX_PX + 2 * BUBBLE_GAP_PX
export const PVP_PLAYER_ACTIVITY_SLOT_HEIGHT_PX =
  BUBBLE_SLOT_PT_PX + BUBBLE_STACK_MAX_PX + BUBBLE_EXIT_BLEED_PX

/** Negative `margin-top` on the wheel wrapper: pull wheel into the activity reserve (tighter than former 40px). */
export const PVP_WHEEL_STACK_OVERLAP_PX =
  BUBBLE_BASE_ROW_PX + BUBBLE_BASE_ROW_PX / 2

/** Outer row: layout + opacity + y. Inner uses absolute + scale so flow height matches ink. */
const ROW_OUTER_TRANSITION = {
  type: "tween" as const,
  layout: { duration: 0.4, ease: [0.22, 1, 0.36, 1] as const },
  opacity: { duration: 0.3, ease: "easeOut" as const },
  y: { duration: 0.3, ease: "easeOut" as const },
}

const ROW_INNER_SCALE_TRANSITION = {
  type: "tween" as const,
  duration: 0.38,
  ease: [0.22, 1, 0.36, 1] as const,
}

interface BubbleRowModel {
  id: string
  text: string
  user?: BetUserData
  amountTon?: number
  /** Non-empty for TELEGRAM_GIFT-backed opens/joins/adds. */
  gifts?: BubbleGiftEntry[]
}

export function PlayerActivity({ roomId }: PlayerActivityProps) {
  const { t } = useI18n()
  const stack = usePvpRoomActivityFeed(roomId)

  /** Last 3 events; render newest-first (top) so the stack reads down toward the wheel like before. */
  const slice = stack.slice(-3)
  const ordered = [...slice].reverse()

  const renderPayload = (
    payload: DerivedBubbleEvent
  ): Omit<BubbleRowModel, "id"> => {
    switch (payload.kind) {
      case "opens":
        return {
          user: payload.user,
          text: t(
            payload.squad
              ? "pvpRoom.bubbles.opensSquad"
              : "pvpRoom.bubbles.opensSolo"
          ),
          amountTon: payload.amount,
          gifts: payload.gifts,
        }
      case "joins":
        return {
          user: payload.user,
          text: t(
            payload.squad
              ? "pvpRoom.bubbles.joinsSquad"
              : "pvpRoom.bubbles.joinsSolo"
          ),
          amountTon: payload.amount,
          gifts: payload.gifts,
        }
      case "adds":
        return {
          user: payload.user,
          text: t("pvpRoom.bubbles.adds"),
          amountTon: payload.delta,
          gifts: payload.gifts,
        }
      case "game_on":
        return { text: t("pvpRoom.bubbles.gameOn") }
      case "tricks_on":
        return { text: t("pvpRoom.bubbles.tricksOn") }
      case "dont_blink":
        return { text: t("pvpRoom.bubbles.dontBlink") }
      case "waiting_intro": {
        const p = payload
        const text = p.hasTricks
          ? p.hasSquadOption
            ? t("pvpRoom.bubbles.waitingSquadSoloTricks")
            : t("pvpRoom.bubbles.waitingSoloTricks")
          : p.hasSquadOption
            ? t("pvpRoom.bubbles.waitingSquadSolo")
            : t("pvpRoom.bubbles.waitingSolo")
        return { text }
      }
      case "trick": {
        const u = payload.user
        const key =
          payload.trickVariant === "push"
            ? "pvpRoom.bubbles.trickPush"
            : payload.trickVariant === "freeze"
              ? "pvpRoom.bubbles.trickFreeze"
              : payload.trickVariant === "smash"
                ? "pvpRoom.bubbles.trickSmash"
                : "pvpRoom.bubbles.trickGeneric"
        return { user: u, text: t(key) }
      }
      default:
        return { text: "" }
    }
  }

  const rows: BubbleRowModel[] = []
  for (const item of ordered) {
    const row = renderPayload(item.payload)
    if (row.text) rows.push({ id: item.id, ...row })
  }

  if (rows.length === 0) {
    return null
  }

  return (
    <div
      className="pointer-events-none box-border flex h-full min-h-0 w-full flex-col overflow-hidden"
      style={{ paddingBottom: BUBBLE_EXIT_BLEED_PX }}
    >
      <div
        className="relative flex w-full shrink-0 flex-col items-end overflow-visible"
        style={{ marginTop: BUBBLE_SLOT_PT_PX }}
      >
        <LayoutGroup id={`player-activity-${roomId}`}>
          {/* popLayout: exiting row leaves layout flow so others never reflow as a 4-row stack (fixes false “4th slot” jump). */}
          <AnimatePresence initial={false} mode="popLayout">
            {rows.map((row, idx) => {
              const bubblesBefore = idx
              const op = VISUAL.opacities[Math.min(bubblesBefore, 2)] ?? 0.5
              const scale = bubbleLayoutScale(bubblesBefore)
              const layoutH = bubbleRowLayoutHeightPx(bubblesBefore)
              const hasPill =
                row.amountTon != null && Number.isFinite(row.amountTon)
              const showAvatar = !!row.user
              const hasGifts = (row.gifts?.length ?? 0) > 0
              const labelCentered = !showAvatar && !hasPill && !hasGifts
              const isLastRow = idx === rows.length - 1

              return (
                <motion.div
                  key={row.id}
                  layout
                  className="relative ml-auto w-full shrink-0"
                  style={{
                    height: layoutH,
                    marginBottom: isLastRow ? 0 : BUBBLE_GAP_PX,
                  }}
                  initial={{ opacity: 0, y: -12 }}
                  animate={{ opacity: op, y: 0 }}
                  exit={{
                    opacity: 0,
                    y: 18,
                    transition: {
                      opacity: { duration: 0.38, ease: "easeOut" as const },
                      y: { duration: 0.36, ease: "easeOut" as const },
                    },
                  }}
                  transition={ROW_OUTER_TRANSITION}
                >
                  <motion.div
                    className="absolute right-0 top-0 w-max origin-top-right will-change-transform"
                    style={{ transformOrigin: "100% 0%" }}
                    animate={{ scale }}
                    transition={ROW_INNER_SCALE_TRANSITION}
                  >
                    <div
                      className={cn(
                        "flex h-10 min-h-10 min-w-[125px] max-w-[min(100%,360px)] shrink-0 flex-row items-center gap-2 overflow-x-clip overflow-y-visible rounded-[24px] rounded-bl-[6px] bg-tertiary-main px-2 py-1.5 shadow-sm",
                        labelCentered && "justify-center px-3"
                      )}
                    >
                      {showAvatar && (
                        <Avatar className={BUBBLE_AVATAR}>
                          <AvatarImage
                            src={
                              row.user!.photoUrl
                                ? resolveAvatarLoadUrl(row.user!.photoUrl)
                                : undefined
                            }
                          />
                          <AvatarFallback
                            className={`bg-muted ${BUBBLE_AVATAR_FALLBACK}`}
                          >
                            {getAvatarInitials(displayName(row.user!, "?"))}
                          </AvatarFallback>
                        </Avatar>
                      )}

                      <span
                        className={cn(
                          "min-w-0 flex-1 overflow-x-hidden overflow-y-visible text-ellipsis whitespace-nowrap text-[14px] font-semibold leading-tight tracking-[-0.5px] text-white",
                          labelCentered ? "text-center" : "text-left"
                        )}
                      >
                        {row.text}
                      </span>

                      {hasGifts && row.gifts && (
                        <div className="flex shrink-0 items-center gap-0.5">
                          <GiftBetThumbnails gifts={row.gifts} tone="bubble" />
                          {hasPill && (
                            <PvpTonAmountPill
                              tonValue={row.amountTon!}
                              format="bubble"
                              cornerStyle="tab"
                            />
                          )}
                        </div>
                      )}

                      {!hasGifts && hasPill && (
                        <PvpTonAmountPill
                          tonValue={row.amountTon!}
                          format="bubble"
                        />
                      )}
                    </div>
                  </motion.div>
                </motion.div>
              )
            })}
          </AnimatePresence>
        </LayoutGroup>
      </div>
    </div>
  )
}
